import { takeEvery } from 'redux-saga/effects';
import {
  Asset,
  COLLECTION_REPO_TOKEN,
  ICollectionRepo,
} from '@integration-frontends/integration/core/model';
import { DI_CONTAINER } from '@integration-frontends/core';
import { put, call, select } from 'typed-redux-saga';
import {
  bulkRemoveAssetsFromCollectionsInit,
  bulkRemoveAssetsFromCollectionsSuccess,
  deleteError,
} from '../actions';
import { createToast } from '@integration-frontends/common/notifications';
import { plural, t } from '@lingui/macro';
import { contentLibraryUploadAssetsSelectors } from '../../../selectors';
import { removeFromPinnedUploads } from '../../../content-library-upload-assets';

interface RemoveAssetsFromCollectionsResponse {
  result: 'accepted' | 'error';
}

function* handler(action: ReturnType<typeof bulkRemoveAssetsFromCollectionsInit>) {
  const collectionRepo: ICollectionRepo = DI_CONTAINER.get(COLLECTION_REPO_TOKEN);
  const { assetIds, collectionId } = action.payload;
  const pinnedUploads: Asset[] = yield select(contentLibraryUploadAssetsSelectors.pinnedUploads);
  const pinnedUploadsBeingDeleted = pinnedUploads.filter((asset) => assetIds.includes(asset.id));
  yield put(
    removeFromPinnedUploads({
      assetIds: pinnedUploadsBeingDeleted.map((asset) => asset.id),
    }),
  );

  try {
    const response: RemoveAssetsFromCollectionsResponse = yield call(
      collectionRepo.removeAssetsFromCollections,
      collectionId,
      assetIds,
    );
    if (response.result === 'accepted') {
      yield put(
        createToast({
          id: `delete-asset-success-${Date.now()}`,
          type: 'success',
          toastContent: {
            contentText: plural(assetIds.length, {
              one: '# file deleted',
              other: '# files deleted',
            }),
          },
        }),
      );
      yield put(bulkRemoveAssetsFromCollectionsSuccess({ assetIds }));
    }
  } catch (error) {
    yield put(
      createToast({
        id: `delete-asset-error-${Date.now()}`,
        type: 'error',
        toastContent: { contentText: t`Error deleting files` },
      }),
    );
    yield put(deleteError());
  }
}

export function* bulkRemoveAssetsFromCollectionsEffects() {
  yield takeEvery(bulkRemoveAssetsFromCollectionsInit, handler);
}
